import moment from "moment-timezone";

import { useContext, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ChatIcon from "@mui/icons-material/Chat";
import LanguageIcon from "@mui/icons-material/Language";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PlaceIcon from "@mui/icons-material/Place";
import RamenDiningIcon from "@mui/icons-material/RamenDining";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import TagIcon from "@mui/icons-material/Tag";
import Skeleton from "@mui/material/Skeleton";
import Button from "@mui/material/Button";

import { useAuth } from "../hooks/useAuth";
import useEvent from "../hooks/useEvent";
import usePageTitle from "../hooks/usePageTitle";

import { ToastContext } from "../Components/ToastProvider";
import LoadingButton from "../Components/LoadingButton";
import LoginCTA from "../Components/LoginCTA";
import Modal from "../Components/Modal";
import CompleteProfileModal from "./CompleteProfileModal";

import "../App.css";

function Event() {
  const { authed, profile } = useAuth();
  const { id } = useParams();

  const { createToast, dismissToast } = useContext(ToastContext);

  const errorToastRef = useRef(null);

  const {
    event,
    error,
    joinEvent,
    hasJoinedEvent,
    unjoinEvent,
    isOwner,
    cancelEvent,
    createMessage,
    loading,
    newMessage,
    setNewMessage,
  } = useEvent(id);

  if (newMessage) {
    createToast("There's a new message on this event", "notice");
    setNewMessage(false);
  }
  const [disabled, setDisabled] = useState(false);
  const [isModalOpen, toggleIsModalOpen] = useState(false);
  const [isUnjoinModalOpen, toggleIsUnjoinModalOpen] = useState(false);
  const [isCompleteProfileModalOpen, toggleIsCompleteProfileModalOpen] =
    useState(false);
  const [isCTAModalOpen, toggleCTAModalOpen] = useState(false);

  const [message, setMessage] = useState("");
  const maxChars = 300;

  const [showErrorMessage, setShowErrorMessage] = useState(false);

  const inThreeHours = moment().add(3, "h");

  const navigate = useNavigate();

  // Don't see the need to always navigate away if error.
  // Should be showing a message and stay on page to try again.
  // If this is used for something else, like I am guessing a wrong page redirect,
  // then we will find out and can put this code elsewhere.
  // Because right now it is making it navigate away when the message is too long.
  // useEffect(() => {
  //   if (error) {
  //     navigate("/events");
  //   }
  // }, [error, navigate]);

  usePageTitle(`${event?.title} | Fudopia | ${event?.city?.title}`);

  function handleJoinEvent({
    commandFromCompleteProfileModal,
    updatedProfile,
  } = {}) {
    const food = updatedProfile?.favoriteFood || profile.favorite_food;
    const g = updatedProfile?.gender || profile.gender;
    const spice = updatedProfile?.spiceLevel || profile.spice_level;
    const ask = updatedProfile?.askMeAbout || profile.ask_me_about;
    const ageRange = updatedProfile?.age || profile.age_range;
    const cuisineList = updatedProfile?.cuisineIds || profile.cuisines;

    const showProfileFields =
      !food || !g || !spice || !ask || !ageRange || !cuisineList?.length;

    if (showProfileFields) {
      if (commandFromCompleteProfileModal) {
        setShowErrorMessage(true);
      } else {
        toggleIsCompleteProfileModalOpen(true);
        setShowErrorMessage(false);
      }
      return false;
    }

    if (errorToastRef.current) {
      dismissToast(errorToastRef.current);
      errorToastRef.current = null;
    }

    setDisabled(true);
    joinEvent(id).then((success) => {
      createToast("You successfully joined this event 😊", "success", true);
      setDisabled(false);
    });
  }

  function handleUnjoinEvent() {
    toggleIsUnjoinModalOpen(false);
    setDisabled(true);
    unjoinEvent(id).then(async (success) => {
      createToast("You succesfully left this event", "success");
      setDisabled(false);
    });
  }

  function handleCancelEvent() {
    toggleIsModalOpen(!isModalOpen);
    setDisabled(true);
    cancelEvent(id).then(async (success) => {
      if (success) {
        createToast("You canceled the event", "success", true);
        navigate(`../../events/`, { replace: true });
      } else {
        createToast("Please try again...", "warning");
      }
      setDisabled(false);
    });
  }

  const handleCreateMessage = (event) => {
    event.preventDefault();
    setDisabled(true);
    const data = {
      event_id: id,
      body: message,
    };

    createMessage(data).then(async (success) => {
      if (success) {
        setMessage("");
      } else {
        createToast("Please try again...", "warning");
      }
      setDisabled(false);
    });
  };
  return (
    <>
      <Modal
        title="You're about to join. Let’s get you in!"
        isOpen={isCTAModalOpen}
        handleDismiss={() => toggleCTAModalOpen(!isCTAModalOpen)}
        description="Looks like you're ready to grab a seat.
Just log in or sign up to save your spot.
✨ First 200 members get a free account for a whole year."
      >
        <div className="btn-inline">
          <Link to="/register">
            <Button
              color="secondary"
              variant="contained"
              onClick={handleCancelEvent}
            >
              Create Free Account
            </Button>
          </Link>
          <Link to="/login">
            <Button
              color="primary"
              variant="contained"
              onClick={() => toggleIsModalOpen(!isModalOpen)}
            >
              Log In
            </Button>
          </Link>
        </div>
      </Modal>
      <Modal
        title="Are you sure about canceling this event?"
        isOpen={isModalOpen}
        handleDismiss={() => toggleIsModalOpen(!isModalOpen)}
      >
        {/* if event is not over 1 hr away, show warning */}
        {!moment(event?.date).isAfter(moment().add(1, "h")) && (
          <p className="centered">
            The meetup starts soon, and attendees may already be on the way.{" "}
            <br />
            If you can, please leave a short update in the event chat <br />
            so everyone knows what’s happening.
          </p>
        )}
        <div className="btn-inline">
          <Button
            color="primary"
            variant="outlined"
            onClick={handleCancelEvent}
          >
            Yes
          </Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={() => toggleIsModalOpen(!isModalOpen)}
          >
            No
          </Button>
        </div>
      </Modal>
      <Modal
        title="Leaving already?"
        isOpen={isUnjoinModalOpen}
        handleDismiss={() => toggleIsUnjoinModalOpen(!isUnjoinModalOpen)}
      >
        {/* if event is not over 1 hr away, show warning */}
        {!moment(event?.date).isAfter(moment().add(1, "h")) ? (
          <p className="centered">
            The meetup starts soon, and others may already be on the way. <br />
            If possible, send a short update in the event chat <br />
            (for example, “Running a bit late” or “I can’t make it today”){" "}
            <br />
            so everyone knows what’s happening.
          </p>
        ) : (
          <>
            <p style={{ textAlign: "center" }}>
              Are you sure you want to leave this event?
            </p>
            <p style={{ marginBottom: 40, textAlign: "center" }}>
              We’ll miss you at the table, but totally understand if plans
              change.
            </p>
          </>
        )}

        <div className="btn-inline">
          <Button
            color="secondary"
            variant="contained"
            onClick={() => toggleIsUnjoinModalOpen(!isUnjoinModalOpen)}
          >
            Keep My Spot
          </Button>
          <Button
            color="primary"
            variant="outlined"
            onClick={handleUnjoinEvent}
          >
            Leave event
          </Button>
        </div>
      </Modal>

      <CompleteProfileModal
        isOpen={isCompleteProfileModalOpen}
        toggleOpen={toggleIsCompleteProfileModalOpen}
        onSave={handleJoinEvent}
        showErrorMessage={showErrorMessage}
      />

      {error && <p className="error-message">Something went wrong...</p>}
      {loading && (
        <div className="block-wrapper">
          <Skeleton animation="wave" height={600}></Skeleton>
        </div>
      )}

      {!loading && event && (
        <>
          <div className="block event">
            <h1>{event.title}</h1>
            <div className="box">
              <div className="info-block">
                <h2 className="subheader">
                  <AccessTimeIcon fontSize="small" className="icon-header" />
                  Date & Time
                </h2>
                {event.date && (
                  <p>
                    {/* We show the event time in the event's timezone for every user, since it's an in person event
              and joining it is only possible at the event's timezone */}
                    {moment(event.date)
                      .tz(event.timezone)
                      .format(
                        authed ? "dddd, MMMM Do · h:mm a" : "dddd, MMMM Do",
                      )}
                    {!authed && (
                      <p>
                        Psst… the time’s visible when you{" "}
                        <Link to="/login">log in</Link>. 🕦
                      </p>
                    )}
                  </p>
                )}
                <h2 className="subheader no-capitalize">
                  <LanguageIcon fontSize="small" className="icon-header " />
                  Event's Language(s)
                </h2>
                <span className="capitalize">{event.languages}</span>
                <h2 className="subheader">
                  <LocationCityIcon fontSize="small" className="icon-header" />
                  City
                </h2>
                <span>{event.location.city.title}</span>
                <h2 className="subheader">
                  <RestaurantIcon fontSize="small" className="icon-header" />
                  Where You’ll Meet
                </h2>
                <Link to={`/restaurants/${event.location.id}`}>
                  <span>{event.location.title}</span>
                </Link>
                <div className="short-text">
                  <h2 className="subheader">
                    <PlaceIcon fontSize="small" className="icon-header" />
                    Address
                  </h2>
                  <Link
                    to={event.location.google_maps}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span>{event.location.address}</span>
                  </Link>
                </div>
              </div>

              <div className="detail-block">
                <h2 className="subheader">
                  <StickyNote2Icon fontSize="small" className="icon-header" />
                  About this meetup
                </h2>
                <p>{event.body}</p>
                <h2 className="subheader">
                  <RamenDiningIcon fontSize="small" className="icon-header" />
                  What are we trying?
                </h2>
                <p className="remark">
                  ✨ This dish or drink sets the tone for the group. Share it,
                  skip it, or just use it to spark conversation.
                </p>
                <Link to={`/dishes/${event.dish?.id}`}>
                  <p>{event.dish?.title}</p>
                  <img
                    src={event.dish?.image_url}
                    alt={event.dish?.title}
                    className="dish-event"
                  />
                </Link>
                {authed ? (
                  <>
                    <h2 className="subheader">
                      <PeopleAltIcon fontSize="small" className="icon-header" />
                      Who's Coming
                    </h2>
                    {event.clients.length > 0 ? (
                      <div className="attendees">
                        {event.clients.map((client, i) => (
                          <Link
                            to={`/users/${client.username}`}
                            key={i}
                            className="attendee-link"
                          >
                            {client.favorite_food ||
                            client.bio ||
                            client.ask_me_about ? (
                              // Full profile card
                              <div className="attendee-card">
                                <div className="attendee-card-top">
                                  <div className="attendee-avatar">🍜</div>
                                  <div className="attendee-info">
                                    <span className="attendee-name">
                                      {client.username}
                                    </span>
                                    <div className="attendee-meta">
                                      {client.gender && (
                                        <span>{client.gender}</span>
                                      )}
                                      {client.gender && client.age_range && (
                                        <span>·</span>
                                      )}
                                      {client.age_range && (
                                        <span>{client.age_range}</span>
                                      )}
                                      {client.spice_level && <span>·</span>}
                                      {client.spice_level && (
                                        <span>{client.spice_level}</span>
                                      )}
                                    </div>
                                  </div>
                                  {client.event_count > 1 && (
                                    <span className="attendee-events">
                                      {client.event_count - 1}{" "}
                                      {client.event_count - 1 === 1
                                        ? "event"
                                        : "events"}
                                    </span>
                                  )}
                                </div>

                                {client.favorite_food && (
                                  <div className="attendee-dish">
                                    🍽️ {client.favorite_food}
                                  </div>
                                )}

                                {client.bio && (
                                  <div className="attendee-bio">
                                    "{client.bio}"
                                  </div>
                                )}

                                {client.ask_me_about && (
                                  <div className="attendee-ask">
                                    <strong>Ask me about</strong>{" "}
                                    {client.ask_me_about}
                                  </div>
                                )}

                                {client.cuisines?.length > 0 && (
                                  <div className="chips">
                                    {client.cuisines.slice(0, 3).map((c) => (
                                      <span
                                        key={c.id}
                                        className="chip chip--display"
                                      >
                                        {c.title}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ) : (
                              // Minimal card — incomplete profile
                              <div className="attendee-card attendee-card--minimal">
                                <div className="attendee-avatar">🍽️</div>
                                <div className="attendee-card--minimal-info">
                                  <span className="attendee-name">
                                    {client.username}
                                  </span>
                                  <div className="incomplete-hint">
                                    Profile not filled out yet
                                  </div>
                                </div>
                                {client.event_count > 1 && (
                                  <span className="attendee-events">
                                    {client.event_count - 1}{" "}
                                    {client.event_count - 1 === 1
                                      ? "event"
                                      : "events"}
                                  </span>
                                )}
                              </div>
                            )}
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <div>
                        <p>This event's waiting for its first foodie!</p>
                        <p>
                          Start the fun. Hit "Join Event" and lead the way 🎉
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <h2 className="subheader">
                      <PeopleAltIcon fontSize="small" className="icon-header" />
                      Participants
                    </h2>
                    {event.client_count > 0 ? (
                      <p>
                        {event.client_count === 1
                          ? "You're not alone. 1 foodie already signed up!"
                          : `${event.client_count} people joined already`}
                      </p>
                    ) : (
                      <p>This event’s waiting for its first foodie!</p>
                    )}
                    {!event.clients && event.client_count === 0 ? (
                      <>
                        <span>
                          <Link to="/register/">Create an account</Link> or{" "}
                          <Link to="/login">log in</Link>to grab your spot! It’s
                          free for the first 200 users! 🎉
                        </span>
                      </>
                    ) : (
                      <>
                        <span>Want to see who’s joining?</span>
                        <span>
                          <br />
                          <Link to="/register/">
                            Create a free account
                          </Link> and <Link to="/login">log in</Link>, it’s free
                          for the first 200 users! 🎉
                        </span>
                      </>
                    )}
                  </>
                )}
                {event.tags?.length > 0 && (
                  <>
                    <h2 className="subheader">
                      <TagIcon fontSize="small" className="icon-header" />
                      Meetup Mood
                    </h2>
                    <ul>
                      {event.tags.map((tag) => (
                        <li key={tag.id}>{tag.title}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </div>

            {/* actions for logged in users */}
            {authed && event.active === true && (
              <>
                {!isOwner ? (
                  <>
                    {!hasJoinedEvent &&
                      moment(event.date).isAfter(inThreeHours) &&
                      event?.clients?.length !== 4 && (
                        <>
                          <div
                            className="button-wrapper"
                            style={{ marginLeft: 0 }}
                          >
                            <p style={{ fontWeight: "bold" }}>
                              ✨ {4 - event.clients.length}{" "}
                              {Number(4 - event.clients.length) === 1
                                ? "spot"
                                : "spots"}{" "}
                              left
                            </p>
                            <LoadingButton
                              color="primary"
                              variant="contained"
                              disabled={disabled}
                              onClick={handleJoinEvent}
                            >
                              Join Event
                            </LoadingButton>
                          </div>
                        </>
                      )}

                    {event?.clients?.length === 4 && !hasJoinedEvent && (
                      <>
                        <div className="section">
                          <p className="big-remark">This event is full</p>
                        </div>
                        <p>
                          ✨ Check out other events or create your own by
                          choosing a dish or restaurant first!
                        </p>
                        <div className="button-wrapper">
                          <Link to="/events/">
                            <Button color="primary" variant="contained">
                              See other events
                            </Button>
                          </Link>
                        </div>
                      </>
                    )}

                    {!hasJoinedEvent &&
                      !moment(event.date).isAfter(inThreeHours) && (
                        <>
                          <div className="section">
                            <p className="big-remark">
                              Sorry, it's too late to join this event!
                            </p>
                          </div>
                          <div className="button-wrapper">
                            <Link to="/events/">
                              <Button color="primary" variant="contained">
                                See other events
                              </Button>
                            </Link>
                          </div>
                        </>
                      )}

                    {hasJoinedEvent && (
                      <div className="button-wrapper">
                        {moment(event.date) <= moment().tz(event.timezone) ? (
                          <Button
                            disabled={true}
                            color="secondary"
                            variant="contained"
                          >
                            Past Event
                          </Button>
                        ) : (
                          <LoadingButton
                            color="primary"
                            variant="outlined"
                            disabled={disabled}
                            onClick={() =>
                              toggleIsUnjoinModalOpen(!isUnjoinModalOpen)
                            }
                          >
                            Leave the Event
                          </LoadingButton>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="button-wrapper">
                    {moment(event.date) <= moment().tz(event.timezone) ? (
                      <Button
                        disabled={true}
                        color="secondary"
                        variant="contained"
                      >
                        Past Event
                      </Button>
                    ) : (
                      <LoadingButton
                        disabled={disabled}
                        onClick={() => toggleIsModalOpen(!isModalOpen)}
                        color="primary"
                        variant="outlined"
                      >
                        Cancel Event
                      </LoadingButton>
                    )}
                  </div>
                )}
              </>
            )}
            {/* For users who are NOT logged in */}
            {!authed &&
              event.active === true &&
              moment(event.date).isAfter(inThreeHours) &&
              event?.client_count !== 4 && (
                <div className="button-wrapper">
                  <p style={{ fontWeight: "bold" }}>
                    ✨ {4 - event.client_count}{" "}
                    {Number(4 - event.client_count) === 1 ? "spot" : "spots"}{" "}
                    left
                  </p>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={() => toggleCTAModalOpen(true)}
                  >
                    Join Event
                  </Button>
                </div>
              )}
            {!authed &&
              event.active === true &&
              moment(event.date).isAfter(inThreeHours) &&
              event?.client_count === 4 && (
                <div className="section">
                  <p className="big-remark">This event is full</p>
                  <p>
                    ✨ Check out other events or create your own by choosing a
                    dish or restaurant first!
                  </p>
                  <div className="button-wrapper">
                    <Link to="/events/">
                      <Button color="primary" variant="contained">
                        See other events
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
            {/* Only show them if they are joined, or it's at least 3 hours before the event time */}
            {hasJoinedEvent && (
              <div className="section discussions">
                <h2>
                  <ChatIcon fontSize="small" className="icon-header" />{" "}
                  Discussions
                </h2>
                <span className="remark">
                  Chat with event participants to ask questions, share updates,
                  or just say hi!
                </span>

                <ul>
                  {event.discussions.map((discussion) => (
                    <li key={discussion.id}>
                      <div className="discussion-wrapper">
                        <span className="discussion-author">
                          {discussion.author.username}
                        </span>
                        <span className="discussion-date">
                          {moment(discussion.created_at)
                            .tz(event.timezone)
                            .format("dddd, MMMM Do · h:mm a")}
                        </span>
                        <p>{discussion.body}</p>
                      </div>
                    </li>
                  ))}
                </ul>
                <form className="add-discussion" onSubmit={handleCreateMessage}>
                  <textarea
                    name="message"
                    id="message"
                    rows="6"
                    required
                    placeholder="Your message"
                    value={message}
                    onChange={(event) => {
                      setMessage(event.target.value);
                    }}
                    maxLength={maxChars}
                  />
                  <p className="remark">
                    {message.length} / {maxChars}
                  </p>
                  <LoadingButton
                    color="primary"
                    type="submit"
                    variant="contained"
                    disabled={disabled}
                  >
                    Send
                  </LoadingButton>
                </form>
              </div>
            )}

            {authed && event.active === false && (
              <div className="section">
                <p className="error-message">
                  This event is canceled or passed already
                </p>
                <div className="button-wrapper">
                  <Link to="/events/">
                    <Button color="primary" variant="contained">
                      See other events
                    </Button>
                  </Link>
                </div>
              </div>
            )}
            {/* end actions for logged in users */}

            <div className="section">
              <Link
                to="/events"
                className="back-button"
                style={{ textDecoration: "underline" }}
              >
                ← Back to the Events
              </Link>
            </div>
          </div>

          {!authed && <LoginCTA />}
        </>
      )}
    </>
  );
}

export default Event;
